import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  aiAnalizerActionQuery,
  aiAnalizerPageQuery,
  aiAnalizerTableQuery,
  aiCommandsQuery,
  createAiAction,
  removeAiAction,
  updateAiAction,
  type AiActionForm,
  type AiAnalizerPage,
  type AiCommand,
  type AiCondition,
} from '../../api/settingsDiagnosticAiAnalizer'
import { categorySubcategoriesQuery, subcategoryOperationsQuery } from '../../api/settingsEaseedeskAdamIntopDefaults'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Easeedesk > AI Analizer Actions > AI Analizer Actions, element cu element
 * fata de `diagnostic/settings/ai_analizer/index.php` + `create_modal.php` +
 * `update_form.php` + fragmentele `*_condition.php` si `AiAnalizerActions()`
 * din `adam_intop_settings.js`: tabelul DataTables (numele, tipul colorat,
 * comenzile si conditiile ca etichete - HTML-ul scris de repository -, cele
 * trei comutatoare, OS, Edit/Delete), fereastra de creare / editare cu
 * numele, tipul, tipurile de tichet si firmele, "Default for all SP", OS,
 * constructorul de CONDITII (operator AND/OR + referinta -> un rand pe
 * conditie, cu campurile fragmentului ei), constructorul de COMENZI (tip ->
 * lista comenzilor tipului, sau mesaj / timp; restart, timeout, scheduled,
 * mandatory), potrivirea de categorie in cascada si cele doua comutatoare.
 */
const TABLE_ID = 'ai-analizer-actions-table'
const ORDER_NAMES = ['action_name', 'action_type', 'commands', 'text_conditions', 'eligible_for_adam_intop', 'default_for_all_sp', 'os', 'action_status', 'id']

const TYPE_COLOUR: Record<string, string> = { '1': 'blue', '2': 'green', '3': 'yellow', '4': 'pink', '5': 'brown', '6': 'orange', '7': 'darkorange', '8': 'purple', '9': 'darkblue', '10': 'darkred', '11': 'quartz', '12': 'red' }
const OS_NAME: Record<string, string> = { '1': 'Windows', '2': 'MacOS', '3': 'Android', '4': 'iOS' }
const REFERENCES = [
  { value: 'text', label: 'TEXT ANALIZER' },
  { value: 'description', label: 'DESCRIPTION ANALIZER' },
  { value: 'image', label: 'IMAGE' },
  { value: 'diagnostic', label: 'DIAGNOSTIC' },
  { value: 'logs', label: 'CRITICAL LOGS' },
  { value: 'sku', label: 'SKU' },
  { value: 'workorder', label: 'WORKORDER' },
]
const TEXT_OPS = ['MATCH', 'LIKE AFTER', 'LIKE BOTH', 'LIKE BEFORE', 'NOT LIKE']
const DIAG_OPS = [...TEXT_OPS, 'FALSE', 'TRUE', '=', '>', '<', '<>']
const COMMAND_TYPES = [
  { value: '1', label: 'CMD' },
  { value: '2', label: 'Agent command' },
  { value: '3', label: 'Collaborate' },
  { value: '4', label: 'Sleep' },
  { value: '5', label: 'BAT' },
  { value: '6', label: 'SHELL' },
  { value: '7', label: 'macOS' },
]

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function yesNo(v: unknown): string {
  return String(v) === '1' ? '<label class="label label-success">Yes</label>' : '<label class="label label-default">No</label>'
}

function columns(): TableColumn[] {
  return [
    { index: 0, title: 'Action Name', orderable: true, visible: true, className: 'align-left', html: (row) => `<button data-id="${Number(row.id)}" class="btn btn-xs bg-success text-uppercase border-0 edit-action"> <i class="fa fa-eye" aria-hidden="true"></i> ${esc(String(row.action_name ?? ''))}</button>` },
    { index: 1, title: 'Action Type', orderable: true, visible: true, width: 160, className: 'align-left', html: (row) => `<label class="label bg-${TYPE_COLOUR[String(row.action_type_id ?? '')] ?? 'grey'}" style="color:#fff"> ${esc(String(row.action_type_name ?? ''))}</label>` },
    { index: 2, title: 'Commands', orderable: true, visible: true, className: 'align-left', html: (row) => String(row.commands_info ?? '') },
    { index: 3, title: 'Active Conditions', orderable: true, visible: true, className: 'align-left', html: (row) => String(row.text_conditions ?? '') },
    { index: 4, title: 'Run by Adam Intop', orderable: true, visible: true, className: 'align-center', html: (row) => yesNo(row.eligible_for_adam_intop) },
    { index: 5, title: 'Default for All SP', orderable: true, visible: true, className: 'align-center', html: (row) => yesNo(row.default_for_all_sp) },
    { index: 6, title: 'OS', orderable: true, visible: true, className: 'align-center', html: (row) => `<label class="label label-default">${OS_NAME[String(row.os ?? '')] ?? 'Unknown'}</label>` },
    { index: 7, title: 'Action Status', orderable: true, visible: true, className: 'align-center', html: (row) => (String(row.action_status) === '1' ? '<label class="label label-success">Active</label>' : '<label class="label label-default">Inactive</label>') },
    {
      index: 8,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 160,
      html: (row) =>
        Number(row.own) === 1
          ? `<button class="btn btn-sm btn-primary edit-action" data-id="${Number(row.id)}"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button> <button class="btn btn-sm btn-default remove-action" data-id="${Number(row.id)}">Delete</button>`
          : `<button class="btn btn-sm btn-primary edit-action" data-id="${Number(row.id)}"><i class="fa fa-eye" aria-hidden="true"></i> View</button>`,
    },
  ]
}

const EMPTY: AiActionForm = {
  action_name: '', action_type: '', run_only_on_ticket_types: [], run_only_on_companies: [], default_for_all_sp: '0', os: '1',
  category: '', subcategory: '', operation: '', eligible_for_adam_intop: false, status: false, condition: [], commands: [],
}
const EMPTY_COMMAND: AiCommand = { type: '', id: '', message: '', time: '', restart: '', timeout: '', scheduled: '', mandatory_to_run: '' }

export function AiAnalizerActionsPage() {
  const client = useQueryClient()
  const page = useQuery(aiAnalizerPageQuery())
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 0, dir: 'desc' }, search: '' })
  const cols = useMemo(() => columns(), [])
  const table = useQuery({
    ...aiAnalizerTableQuery({
      draw: 1, start: state.start, length: state.length, orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'id', orderDir: state.order.dir, search: state.search,
    }),
    placeholderData: keepPreviousData,
  })
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const redraw = () => client.invalidateQueries({ queryKey: ['settings', 'diagnostic', 'ai_analizer'] })

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const edit = target.closest('.edit-action')
    if (edit instanceof HTMLElement) {
      setEditing(Number(edit.dataset.id ?? 0))
      return
    }
    const remove = target.closest('.remove-action')
    if (remove instanceof HTMLElement) {
      if (!window.confirm('Are you sure?')) return
      void removeAiAction(Number(remove.dataset.id ?? 0))
        .then(() => {
          toast('Action removed', 'AI Actions')
          void redraw()
        })
        .catch(() => toast('Something went wrong', 'AI Actions', 'error'))
    }
  }

  return (
    <div onClick={onClick}>
      <div className="row">
        <div className="col-md-6">
          <h4>
            AI Analizer Actions [for <strong> {page.data.providerName} </strong> SP]
          </h4>
        </div>
        <div className="col-md-6 align-right">
          <button type="button" className="btn btn-default add-ai-action" onClick={() => setCreating(true)}>
            Add action
          </button>
        </div>
      </div>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={cols}
        rows={(table.data?.data ?? []) as Record<string, unknown>[]}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
        emptyHint="An AI action matches a ticket by its text, image or diagnostic and suggests commands."
      />

      <ActionModal id="aiActionModal" title="New action" open={creating} action={0} page={page.data} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); toast('Action created', 'AI Actions'); void redraw() }} />
      <ActionModal id="aiActionUpdateModal" title="Update action" open={editing > 0} action={editing} page={page.data} onClose={() => setEditing(0)} onSaved={() => { setEditing(0); toast('Action updated', 'AI Actions'); void redraw() }} />
    </div>
  )
}

function ActionModal({ id, title, open, action, page, onClose, onSaved }: { id: string; title: string; open: boolean; action: number; page: AiAnalizerPage; onClose(): void; onSaved(): void }) {
  const current = useQuery({ ...aiAnalizerActionQuery(action), enabled: open && action > 0 })
  const [form, setForm] = useState<AiActionForm>(EMPTY)
  const [newOperator, setNewOperator] = useState<'AND' | 'OR'>('AND')
  const [newReference, setNewReference] = useState('text')
  const [busy, setBusy] = useState(false)
  const subcategories = useQuery(categorySubcategoriesQuery(Number(form.category)))
  const operations = useQuery(subcategoryOperationsQuery(Number(form.subcategory)))

  useEffect(() => {
    if (!open) {
      setForm(EMPTY)
      return
    }
    const d = current.data
    if (!d) return
    const a = d.action
    setForm({
      action_name: String(a.action_name ?? ''),
      action_type: String(a.action_type ?? ''),
      run_only_on_ticket_types: d.run_only_on_ticket_types,
      run_only_on_companies: d.run_only_on_companies,
      default_for_all_sp: String(a.default_for_all_sp ?? '0'),
      os: String(a.os ?? '1'),
      category: d.category > 0 ? String(d.category) : '',
      subcategory: d.subcategory > 0 ? String(d.subcategory) : '',
      operation: d.operation > 0 ? String(d.operation) : '',
      eligible_for_adam_intop: String(a.eligible_for_adam_intop) === '1',
      status: String(a.status) === '1',
      condition: d.conditions.map((c) => ({
        reference: String(c.reference ?? 'text').toLowerCase(),
        operator: String(c.operator ?? 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
        action_condition: String(c.action_condition ?? 'MATCH'),
        value: String(c.value ?? ''),
        rating: String(c.rating ?? '0'),
        scope: String(c.scope ?? ''),
      })),
      commands: d.commands.map((k) => ({
        type: String(k.command_type ?? ''),
        id: k.command_id === null || k.command_id === undefined ? '' : String(k.command_id),
        message: String(k.command_message ?? ''),
        time: k.command_time === null || k.command_time === undefined ? '' : String(k.command_time),
        restart: k.command_restart === null || k.command_restart === undefined ? '' : String(k.command_restart),
        timeout: k.command_timeout === null || k.command_timeout === undefined ? '' : String(k.command_timeout),
        scheduled: String(k.command_scheduled ?? ''),
        mandatory_to_run: k.mandatory_to_run === null || k.mandatory_to_run === undefined ? '' : String(k.mandatory_to_run),
      })),
    })
  }, [open, current.data])

  const readOnly = action > 0 && current.data !== undefined && !current.data.own
  const set = (patch: Partial<AiActionForm>) => setForm({ ...form, ...patch })

  const addCondition = () => {
    set({ condition: [...form.condition, { reference: newReference, operator: newOperator, action_condition: 'MATCH', value: '', rating: '0', scope: '' }] })
  }
  const setCondition = (i: number, patch: Partial<AiCondition>) => set({ condition: form.condition.map((c, j) => (j === i ? { ...c, ...patch } : c)) })
  const dropCondition = (i: number) => set({ condition: form.condition.filter((_, j) => j !== i) })
  const setCommand = (i: number, patch: Partial<AiCommand>) => set({ commands: form.commands.map((k, j) => (j === i ? { ...k, ...patch } : k)) })
  const dropCommand = (i: number) => set({ commands: form.commands.filter((_, j) => j !== i) })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (readOnly) return
    setBusy(true)
    const call = action > 0 ? updateAiAction(action, form) : createAiAction(form)
    void call
      .then(() => { setBusy(false); onSaved() })
      .catch((error: unknown) => {
        setBusy(false)
        toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong', 'AI Actions', 'error')
      })
  }

  const label = { fontSize: 12 }

  return (
    <Modal id={id} labelledBy={`${id}Label`} open={open} onClose={onClose} title={title} large>
      {action > 0 && !current.data ? (
        <Spinner />
      ) : (
        <form id={action > 0 ? 'updateAiActionForm' : 'newAiActionForm'} onSubmit={submit}>
          <div className="modal-body">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>Action name</label>
                  <input className="form-control" type="text" name="action_name" required value={form.action_name} onChange={(e) => set({ action_name: e.target.value })} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>Action type</label>
                  <select name="action_type" className="form-control" required value={form.action_type} onChange={(e) => set({ action_type: e.target.value })}>
                    <option value="">Select...</option>
                    {page.actionTypes.map((t) => (
                      <option key={t.ID} value={String(t.ID)}>{t.type_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Run only on this ticket types </label>
                  <SelectPicker id={`${id}-types`} name="run_only_on_ticket_types[]" className="form-control" title="Nothing selected" multiple options={page.all_ticket_types.map((t) => ({ value: String(t.ID), label: `${t.short_name} (${t.type_name})` }))} value={form.run_only_on_ticket_types} onChange={(v) => set({ run_only_on_ticket_types: v })} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="control-label">Run only on these companies </label>
                  <SelectPicker id={`${id}-companies`} name="run_only_on_companies[]" className="form-control" title="Nothing selected" multiple options={page.allCompaniesSp.map((c) => ({ value: String(c.ID), label: c.name }))} value={form.run_only_on_companies} onChange={(v) => set({ run_only_on_companies: v })} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>This Action is Default for all SP</label>
                  <select name="default_for_all_sp" className="form-control" value={form.default_for_all_sp} onChange={(e) => set({ default_for_all_sp: e.target.value })}>
                    <option value="0">No</option>
                    <option value="1">Yes</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>OS</label>
                  <select name="os" className="form-control" value={form.os} onChange={(e) => set({ os: e.target.value })}>
                    {Object.entries(OS_NAME).map(([v, n]) => (
                      <option key={v} value={v}>{n === 'MacOS' ? 'macOS' : n}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bs-conditions" style={{ marginBottom: 10 }}>
              {form.condition.map((c, i) => (
                <ConditionRow key={i} index={i} condition={c} onChange={(patch) => setCondition(i, patch)} onRemove={() => dropCondition(i)} />
              ))}
            </div>
            <div className="bs-callout bs-callout-no-padding bs-callout-primary">
              <div className="bs-condition-title">Conditions</div>
              <div className="row" style={{ padding: '6px 12px' }}>
                <div className="col-md-3">
                  <select className="form-control" id="new-condition-operator" value={newOperator} onChange={(e) => setNewOperator(e.target.value === 'OR' ? 'OR' : 'AND')}>
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <select className="form-control" id="new-condition-reference" value={newReference} onChange={(e) => setNewReference(e.target.value)}>
                    {REFERENCES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <button type="button" className="btn btn-success pull-right addCondition" onClick={addCondition}>
                    <i className="fas fa-plus"></i> Add condition
                  </button>
                </div>
              </div>
            </div>

            <div className="bs-callout bs-callout-no-padding bs-callout-primary" style={{ marginTop: 10 }}>
              <div className="bs-condition-title">Commands</div>
              {form.commands.map((k, i) => (
                <CommandRow key={i} index={i} command={k} onChange={(patch) => setCommand(i, patch)} onRemove={() => dropCommand(i)} />
              ))}
              <div style={{ padding: '6px 12px' }}>
                <button type="button" className="btn btn-success addCommandCondition" onClick={() => set({ commands: [...form.commands, { ...EMPTY_COMMAND }] })}>
                  <i className="fas fa-plus"></i> Add command
                </button>
              </div>
            </div>

            <div className="row" style={{ marginTop: 10 }}>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>Match Ticket Category</label>
                  <select name="category" className="form-control" required value={form.category} onChange={(e) => set({ category: e.target.value, subcategory: '', operation: '' })}>
                    <option value="">Select...</option>
                    {page.categories.map((c) => (
                      <option key={c.ID} value={String(c.ID)}>{c.category_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>Match Ticket Subcategory</label>
                  <select name="subcategory" className="form-control " value={form.subcategory} onChange={(e) => set({ subcategory: e.target.value, operation: '' })}>
                    <option value="">Select...</option>
                    {(subcategories.data?.subcategories ?? []).map((s) => (
                      <option key={s.ID} value={String(s.ID)}>{s.subcategory_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="label bg-default color-white" style={label}>Match Ticket Operation</label>
                  <select name="operation" className="form-control" value={form.operation} onChange={(e) => set({ operation: e.target.value })}>
                    <option value="">Select...</option>
                    {(operations.data?.operations ?? []).map((o) => (
                      <option key={o.ID} value={String(o.ID)}>{o.operation_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <input type="checkbox" className="switchery" name="eligible_for_adam_intop" checked={form.eligible_for_adam_intop} onChange={(e) => set({ eligible_for_adam_intop: e.target.checked })} />{' '}
                  <label className="control-label">
                    {' '}Eligible for <b> <span className="label" style={{ color: '#FFF', backgroundColor: '#31B724', fontSize: 15 }}> Adam Intop</span></b>{' '}
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <input type="checkbox" className="switchery" name="status" checked={form.status} onChange={(e) => set({ status: e.target.checked })} />{' '}
                  <label className="control-label"> Activate Action </label>
                </div>
              </div>
            </div>
            {readOnly && (
              <div className="alert alert-info" style={{ margin: 0 }}>
                <i className="fas fa-info-circle" aria-hidden="true"></i> This is a platform default action: it can be viewed here, not changed.
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-default" onClick={onClose}>Close</button>{' '}
            {!readOnly && (
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {action > 0 ? 'Update' : 'Save'}
              </button>
            )}
          </div>
        </form>
      )}
    </Modal>
  )
}

/** `*_condition.php`: acelasi rand, cu operatorii si campurile referintei. */
function ConditionRow({ index, condition, onChange, onRemove }: { index: number; condition: AiCondition; onChange(patch: Partial<AiCondition>): void; onRemove(): void }) {
  const isDiag = condition.reference === 'diagnostic'
  const ops = isDiag ? DIAG_OPS : TEXT_OPS
  const n = `condition[${index}]`
  return (
    <div className="bs-condition">
      <div className="bs-callout bs-callout-no-padding">
        <div className="bs-condition-title">
          {condition.operator} {condition.reference.toUpperCase()}
        </div>
        <div style={{ display: 'inline-block', verticalAlign: 'middle', width: '86%' }}>
          <div className="row">
            {isDiag && (
              <div className="col-md-2">
                <input type="text" className="form-control" name={`${n}[scope]`} placeholder="scope" value={condition.scope} onChange={(e) => onChange({ scope: e.target.value })} />
              </div>
            )}
            <div className="col-md-2">
              <select className="form-control" name={`${n}[action_condition]`} value={condition.action_condition} onChange={(e) => onChange({ action_condition: e.target.value })}>
                {ops.map((op) => (
                  <option key={op} value={op}>{op === '<>' ? '!=' : op}</option>
                ))}
              </select>
            </div>
            <div className={isDiag ? 'col-md-4' : 'col-md-6'}>
              <input required type="text" className="form-control" name={`${n}[value]`} value={condition.value} onChange={(e) => onChange({ value: e.target.value })} />
              <input type="hidden" name={`${n}[operator]`} value={condition.operator} />
              <input type="hidden" name={`${n}[reference]`} value={condition.reference} />
            </div>
            <div className="col-md-2">
              <select className="form-control" name={`${n}[rating]`} value={condition.rating} onChange={(e) => onChange({ rating: e.target.value })}>
                {Array.from({ length: 11 }, (_, i) => (
                  <option key={i} value={String(i)}>{i}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 align-right">
              <button type="button" className="btn btn-danger remove-condition" onClick={onRemove}>
                <i className="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Blocul unei comenzi din `create_modal.php` / `update_form.php`. */
function CommandRow({ index, command, onChange, onRemove }: { index: number; command: AiCommand; onChange(patch: Partial<AiCommand>): void; onRemove(): void }) {
  const type = Number(command.type)
  const list = useQuery(aiCommandsQuery(type))
  const n = `commands[${index}]`
  const grouped = list.data?.commands ?? []
  return (
    <div className="row" style={{ padding: '6px 12px' }}>
      <div className="col-md-3">
        <select name={`${n}[type]`} className="form-control select-command-type" value={command.type} onChange={(e) => onChange({ type: e.target.value, id: '', message: '', time: '' })}>
          <option value="">- command type -</option>
          {COMMAND_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        {[1, 2, 5, 6, 7].includes(type) && (
          <select name={`${n}[id]`} className="form-control new-command-select" value={command.id} onChange={(e) => onChange({ id: e.target.value })}>
            <option value="">- command -</option>
            {grouped.map((c) => (
              <option key={c.ID} value={String(c.ID)}>{c.group_name ? `${c.group_name} / ` : ''}{c.command_title}</option>
            ))}
          </select>
        )}
        {type === 3 && <input type="text" name={`${n}[message]`} className="form-control" placeholder="message" value={command.message} onChange={(e) => onChange({ message: e.target.value })} />}
        {type === 4 && <input type="number" name={`${n}[time]`} className="form-control" placeholder="units time" value={command.time} onChange={(e) => onChange({ time: e.target.value })} />}
      </div>
      <div className="col-md-1">
        <select name={`${n}[restart]`} className="form-control select-command-restart" value={command.restart} onChange={(e) => onChange({ restart: e.target.value })}>
          <option value="">- restart -</option>
          <option value="1">YES</option>
          <option value="0">NO</option>
        </select>
      </div>
      <div className="col-md-1">
        <input type="number" className="form-control" name={`${n}[timeout]`} placeholder="timeout (seconds)" value={command.timeout} onChange={(e) => onChange({ timeout: e.target.value })} />
      </div>
      <div className="col-md-1">
        <input type="text" className="form-control" name={`${n}[scheduled]`} placeholder="scheduled" value={command.scheduled} onChange={(e) => onChange({ scheduled: e.target.value })} />
      </div>
      <div className="col-md-1">
        <select name={`${n}[mandatory_to_run]`} className="form-control select-command-mandatory" value={command.mandatory_to_run} onChange={(e) => onChange({ mandatory_to_run: e.target.value })}>
          <option value="">- mandatory to run -</option>
          <option value="1">YES</option>
          <option value="0">NO</option>
        </select>
      </div>
      <div className="col-md-1 align-right">
        <button type="button" className="btn btn-danger remove-command" onClick={onRemove}>
          <i className="fas fa-trash"></i>
        </button>
      </div>
    </div>
  )
}
